// src/annihilation/stats.js
// ============================================================
//  ANNIHILATION — stat math.
//  Source of truth: ANNIHILATION_GATING_DOCTRINE (PRIME addendum)
//  Parts 1 & 4. Where this file and ANNIHILATION_SYSTEMS_SPEC (v2)
//  disagree, the Doctrine wins — PRIME is always canon (§4.1).
//
//  Numbers here are launch-tuning starting points; the SHAPES
//  (flat subtraction, piecewise-linear scaling, TRANSCRIBED
//  Lyt/Lyf allotment) are architectural commitments. Bump a
//  number freely; changing a shape is a caP'n conversation.
//
//  Every value below is a DERIVING FUNCTION or a single table —
//  no parallel copies. UI, combat, and saves all read from here.
//
//  ⚑⚑ BATCH GATE-1 — THE STAT REWRITE (caP'n, Jul 25 2026) ⚑⚑
//  The Gating Doctrine rewrote the bottom of this file:
//    1) AENERGY is now the canon umbrella word for the four
//       stats. Locked term (vyren — display may retune, the
//       concept never does).
//    2) THE TRANSCRIPTION LAW. A being's Lyt and Lyf at any tier
//       are READ FROM ITS TERROR CARD and never derived. All
//       thirty-six Escendants across the twelve starter decks
//       sum to their tier's total — 8 at Beb, 9 at Champion,
//       10 at Lord, without one exception. If a card and a doc
//       disagree, THE CARD WINS.
//    3) Natural Lyt/Lyf therefore DO NOT GROW PER LEVEL AT ALL.
//       The Spec's +0.2 Lyf / +0.1 Lyt table is VOID. Ascension
//       is the only natural movement, and it REDISTRIBUTES (the
//       Tiger loses a Lyf to gain two Lyt at Lord) rather than
//       simply adding.
//    4) Anima and Chao still grow per level, and now also take a
//       guaranteed LUMP at each Ascent — the Annihilation
//       difference from the card game, where you get one point.
//    5) THE CIRCUMVENTION LAW. 8/9/10 is the NATURAL ceiling,
//       not an absolute one. Items, boosts, Tomes, gear and
//       Heresy all push past it and are meant to. STAT_CAPS
//       therefore describes the ITEM-BOOSTED ceiling, rescaled
//       from the old 200/300 (which was ~20× out of scale
//       against a natural max of 10 — roadmap §4.34).
//
//  ⚑ Jul 21 balance pass (ECON-1) survives intact: XP curve ÷5,
//  base Anima raised for swarm survivability, Lyt regen 1.8s.
//  All three are still one-number dials.
// ============================================================

// ------------------------------------------------------------
// TIERS — the Ascent. Beb → Champion → Lord.
// ------------------------------------------------------------
export const TIERS = {
  BEB: "beb",
  CHAMPION: "champion",
  LORD: "lord",
};

export const TIER_BOUNDS = {
  [TIERS.BEB]: { min: 1, max: 30 },
  [TIERS.CHAMPION]: { min: 30, max: 60 },
  [TIERS.LORD]: { min: 60, max: Infinity }, // no strict cap at launch
};

export const TIER_ORDER = [TIERS.BEB, TIERS.CHAMPION, TIERS.LORD];

export const tierForLevel = (level) => {
  if (level < 30) return TIERS.BEB;
  if (level < 60) return TIERS.CHAMPION;
  return TIERS.LORD;
};

// ------------------------------------------------------------
// AENERGY — the umbrella word for the four stats (Doctrine
// Part 1). Locked term. It is correct to say "that U'hlti costs
// a lot of Aenergy" when it costs Chao and Anima both.
//
// Two of the four are SPENDABLE POOLS (you pay out of them);
// two are not. Lyf is flat damage reduction — it is a threshold
// you must HOLD, never a currency you spend. Lyt is a small
// stamina pool that pays for swings, so it is spendable.
// Every gate in the game reads these lists, never a hardcoded
// stat-per-slot table (THE FLUIDITY LAW).
// ------------------------------------------------------------
export const AENERGY = {
  ANIMA: "anima", // Life. The fail state.
  CHAO: "chao", //   The casting pool.
  LYF: "lyf", //     Flat damage reduction.
  LYT: "lyt", //     Attack stamina.
};

export const AENERGY_IDS = [AENERGY.ANIMA, AENERGY.CHAO, AENERGY.LYF, AENERGY.LYT];

/** Which Aenergy can actually be PAID out of a live pool. */
export const SPENDABLE_AENERGY = [AENERGY.ANIMA, AENERGY.CHAO, AENERGY.LYT];

export const AENERGY_LABEL = {
  [AENERGY.ANIMA]: "Anima",
  [AENERGY.CHAO]: "Chao",
  [AENERGY.LYF]: "Lyf",
  [AENERGY.LYT]: "Lyt",
};

// ------------------------------------------------------------
// THE ALLOTMENT — Lyt + Lyf sum to a fixed total per tier.
// Inherited straight from Terror; obeyed by every one of the
// thirty-six cards read. Ascension grants the +1 AND (unlike
// Terror) a lump of Anima and Chao on top.
// ------------------------------------------------------------
export const AENERGY_ALLOTMENT = {
  [TIERS.BEB]: 8,
  [TIERS.CHAMPION]: 9,
  [TIERS.LORD]: 10,
};

// ------------------------------------------------------------
// THE TRANSCRIPTION TABLE — beingId → per-tier { lyt, lyf }.
// This is NOT derived and NEVER will be. It is a reading of the
// Terror TCG cards. Every row sums to its tier's allotment.
//
// APPEND-ONLY (taxonomy-file law): the eight beings below the
// launch four are recorded now, from their cards, so that when
// they ship it is a data read and not a research session. Their
// ids follow the vyren pattern and are set here permanently.
// ------------------------------------------------------------
const alot = (bebLyt, bebLyf, chLyt, chLyf, loLyt, loLyf) => ({
  [TIERS.BEB]: { lyt: bebLyt, lyf: bebLyf },
  [TIERS.CHAMPION]: { lyt: chLyt, lyf: chLyf },
  [TIERS.LORD]: { lyt: loLyt, lyf: loLyf },
});

export const BEING_ALLOTMENT = {
  // ── the four launch beings ────────────────────────────────
  // Spectral Tiger — Beb → Champion → Lord
  spectral_tiger: alot(6, 2, 6, 3, 8, 2),
  // Chaos Dragon — ZERO Lyf at every tier, forever. The purest
  // glass cannon in the roster (roadmap §4.33). Card wins.
  chaos_dragon: alot(8, 0, 9, 0, 10, 0),
  // Z4Z — Demon → Wizard → H'ehll's Sword. Note it LOSES a Lyt
  // at Champion to buy its first armor.
  z4z: alot(8, 0, 7, 2, 8, 2),
  // Xulan — Wanderer → Surgeon → Shadow. Sheds all defense at
  // Lord to become pure edge (roadmap §4.31, resolved by card).
  xulan: alot(6, 2, 7, 2, 10, 0),

  // ── recorded for when they ship (append-only) ─────────────
  shadow_fox: alot(2, 6, 3, 6, 4, 6), //        Beast / Phantom
  inverted_serpent: alot(4, 4, 5, 4, 6, 4), //  Beast / Phantom
  h01: alot(2, 6, 2, 7, 9, 1), //               Space
  h09: alot(5, 3, 5, 4, 10, 0), //              Space
  antdroid: alot(3, 5, 3, 6, 3, 7), //          Time
  dg0: alot(2, 6, 2, 7, 2, 8), //               Time
  lucky_dragon: alot(7, 1, 6, 3, 7, 3), //      Dragon
  premier_dragon: alot(4, 4, 5, 4, 5, 5), //    Dragon
};

/** The card-true { lyt, lyf } for a being at a tier. */
export const allotmentFor = (beingId, tier) =>
  BEING_ALLOTMENT[beingId]?.[tier] || null;

/** The card-true { lyt, lyf } for a being at a level. */
export const allotmentAtLevel = (beingId, level) =>
  allotmentFor(beingId, tierForLevel(level));

// ------------------------------------------------------------
// THE FOUR STATS — starting values at Beb tier (level 1).
//
// `anima` and `chao` are BASES that grow (ECON-1 dials — anima
// was raised ~+50% over spec for swarm survivability). `lyf` and
// `lytCap` are the Beb-tier TRANSCRIPTION, mirrored out of
// BEING_ALLOTMENT above so that character-select and any other
// "what is this being" reader gets card-true numbers without
// knowing about tiers. They do NOT grow — see statsAtLevel.
//
// ⚑ RULED (Doctrine Part 13 §4c): the Chaos Dragon gets NO
// Anima compensation for its Lyf 0. caP'n: it protects itself
// through Primeras, Imperas and Tomes like any other build.
// "That's the point of this game — being able to build whatever
// type of build you want." Glass cannon is a legitimate answer,
// not a hole to patch.
// ------------------------------------------------------------
const bebStats = (beingId, anima, chao) => {
  const a = BEING_ALLOTMENT[beingId][TIERS.BEB];
  return { anima, chao, lyf: a.lyf, lytCap: a.lyt };
};

export const BEING_BASE_STATS = {
  spectral_tiger: bebStats("spectral_tiger", 120, 50),
  chaos_dragon: bebStats("chaos_dragon", 150, 40),
  z4z: bebStats("z4z", 105, 60),
  xulan: bebStats("xulan", 135, 70),
};

// Base Attack: Systems Spec's worked example gives Spectral
// Tiger base 5. Per-being base Attack for the other three isn't
// specced in v2 — they default to 5 pending caP'n tuning.
export const BEING_BASE_ATTACK = {
  spectral_tiger: 5,
  chaos_dragon: 5, // TODO(caP'n tuning): not yet specced
  z4z: 5, //          TODO(caP'n tuning): not yet specced
  xulan: 5, //        TODO(caP'n tuning): not yet specced
};

// ------------------------------------------------------------
// STAT GROWTH PER LEVEL — automatic, independent of skill points.
//
// ⚑ GATE-1: lyf and lyt are now ZERO at every tier and stay
// zero. This is not a tuning choice — the Doctrine voided the
// Spec's +0.2/+0.1 growth table outright, because natural
// Lyt/Lyf move ONLY at Ascension and only as the cards say.
// The keys are kept (rather than deleted) so that any reader
// asking "how much Lyf per level" gets the honest answer 0
// instead of undefined.
// ------------------------------------------------------------
export const STAT_GROWTH = {
  [TIERS.BEB]: { anima: 4, chao: 2, lyf: 0, lyt: 0 },
  [TIERS.CHAMPION]: { anima: 5, chao: 3, lyf: 0, lyt: 0 },
  [TIERS.LORD]: { anima: 6, chao: 4, lyf: 0, lyt: 0 },
};

// ------------------------------------------------------------
// THE ASCENSION LUMP — the Annihilation difference. In the card
// game an Ascent hands you one point; here the whole pool swells.
// Granted once, at the level the Ascent happens, and carried
// forever after. DIALS, both rows.
// ------------------------------------------------------------
export const ASCENSION_LUMP = {
  [TIERS.CHAMPION]: { anima: 40, chao: 30 }, // Beb → Champion, L30
  [TIERS.LORD]: { anima: 60, chao: 50 }, //    Champion → Lord, L60
};

/** Total Ascension lump earned by a given level (cumulative). */
export const ascensionLumpAtLevel = (level) => {
  let anima = 0;
  let chao = 0;
  if (level >= TIER_BOUNDS[TIERS.CHAMPION].min) {
    anima += ASCENSION_LUMP[TIERS.CHAMPION].anima;
    chao += ASCENSION_LUMP[TIERS.CHAMPION].chao;
  }
  if (level >= TIER_BOUNDS[TIERS.LORD].min) {
    anima += ASCENSION_LUMP[TIERS.LORD].anima;
    chao += ASCENSION_LUMP[TIERS.LORD].chao;
  }
  return { anima, chao };
};

// ------------------------------------------------------------
// ATTACK SCALING — piecewise linear. Spec table slopes, verbatim:
// Beb +1.5/level · Champion +2/level · Lord +2.5/level, on a
// base of 5.
//
// ⚑ FLAGGED DISCREPANCY (do not "fix" silently): the spec's
// worked examples chain their own midpoints inconsistently
// (L45 "~57", L75 "~94" don't follow from base 5 + the table
// slopes). The TABLE SLOPES are implemented here as the law;
// the examples are treated as loose "~" illustrations. Logged
// in ANNIHILATION_ROADMAP §4.3 for a tuning-pass reconciliation.
// ------------------------------------------------------------

/** Sum a piecewise-per-level rate from level 0 up to `level`. */
const piecewiseSum = (level, rates /* {beb, champion, lord} */) => {
  const bebLevels = Math.min(level, 30);
  const champLevels = Math.max(0, Math.min(level, 60) - 30);
  const lordLevels = Math.max(0, level - 60);
  return (
    bebLevels * rates.beb + champLevels * rates.champion + lordLevels * rates.lord
  );
};

/** Base (ungeared) Attack at a level. */
export const attackAtLevel = (beingId, level) =>
  Math.round(
    (BEING_BASE_ATTACK[beingId] ?? 5) +
      piecewiseSum(level, { beb: 1.5, champion: 2, lord: 2.5 })
  );

/**
 * Base (ungeared, un-Heretical) full stat block at a level.
 *
 * THIS IS THE MAX POOL. Both halves of the Two-Gate Rule read
 * from it: purchase checks a node's aenergyReq against THIS,
 * activation checks the live pool the controller derives from it.
 *
 * Anima/Chao: base + per-level growth + Ascension lump.
 * Lyf/Lyt:    transcribed. Flat. No growth, ever.
 */
export const statsAtLevel = (beingId, level) => {
  const base = BEING_BASE_STATS[beingId];
  const allot = allotmentAtLevel(beingId, level);
  if (!base || !allot) return null;
  const lump = ascensionLumpAtLevel(level);
  const anima = base.anima + piecewiseSum(level, { beb: 4, champion: 5, lord: 6 }) + lump.anima;
  const chao = base.chao + piecewiseSum(level, { beb: 2, champion: 3, lord: 4 }) + lump.chao;
  return {
    anima: capStat("anima", Math.round(anima)),
    chao: capStat("chao", Math.round(chao)),
    // THE TRANSCRIPTION LAW — read from the card, never derived.
    lyf: allot.lyf,
    lytCap: allot.lyt,
    attack: attackAtLevel(beingId, level),
  };
};

/**
 * The stat block as a plain AENERGY pool — { anima, chao, lyf,
 * lyt }. This is the shape every Aenergy gate consumes, so no
 * caller has to remember that the block spells it `lytCap`.
 */
export const aenergyPool = (blockOrBeingId, level) => {
  const block =
    typeof blockOrBeingId === "string"
      ? statsAtLevel(blockOrBeingId, level)
      : blockOrBeingId;
  if (!block) return null;
  return {
    anima: block.anima ?? 0,
    chao: block.chao ?? 0,
    lyf: block.lyf ?? 0,
    lyt: block.lytCap ?? block.lyt ?? 0,
  };
};

// ------------------------------------------------------------
// STAT CAPS — soft cap (every point past it counts half) and
// hard cap (no further gain).
//
// ⚑ GATE-1 RESCALE (roadmap §4.34): these describe the
// ITEM-BOOSTED ceiling, not the natural one. Natural Lyf and Lyt
// live in 0–10 now, so the Spec's Lyf 200/300 meant
// "circumvention = a twentyfold multiplier," which isn't
// circumvention, it's a different game. Rescaled so a fully
// geared Lord feels transformed without making the natural
// allotment decorative. DIALS — the scale is the point, not the
// exact numbers.
// ------------------------------------------------------------
export const STAT_CAPS = {
  anima: { soft: 1500, hard: 2500 },
  chao: { soft: 500, hard: 800 },
  lyf: { soft: 25, hard: 40 }, //  ⚑ was 200 / 300
  lyt: { soft: 18, hard: 24 }, //  ⚑ was 15 / 20
};

/** Apply soft/hard capping to a raw stat total (base+gear+tree). */
export const capStat = (statName, rawValue) => {
  const caps = STAT_CAPS[statName];
  if (!caps) return rawValue;
  if (rawValue <= caps.soft) return rawValue;
  const softened = caps.soft + (rawValue - caps.soft) / 2;
  return Math.min(softened, caps.hard);
};

// ------------------------------------------------------------
// XP CURVE — piecewise linear, never exponential (spec law).
// XP to go from level N to N+1 = XP_TIER_RATE[tier] × N.
//
// ⚑ BALANCE PASS (Jul 21): rates ÷5 vs the original spec
// (500 / 2000 / 5000). The 1 : 4 : 10 ratio between tiers is
// preserved, so the pacing "shape" is intact — only the overall
// speed changed. These three numbers ARE the leveling-speed
// dial: raise them to slow the climb, lower to quicken it.
// ------------------------------------------------------------
export const XP_TIER_RATE = {
  [TIERS.BEB]: 100, //      was 500 (spec)
  [TIERS.CHAMPION]: 400, // was 2000 (spec)
  [TIERS.LORD]: 1000, //    was 5000 (spec)
};

export const xpToNextLevel = (level) => {
  if (level < 30) return XP_TIER_RATE[TIERS.BEB] * level;
  if (level < 60) return XP_TIER_RATE[TIERS.CHAMPION] * level;
  return XP_TIER_RATE[TIERS.LORD] * level;
};

/** Total cumulative XP required to REACH a level from level 1. */
export const totalXpToLevel = (level) => {
  let total = 0;
  for (let l = 1; l < level; l++) total += xpToNextLevel(l);
  return total;
};

/** Derive current level from lifetime XP (the save stores XP; level derives). */
export const levelForXp = (xp) => {
  let level = 1;
  let remaining = xp;
  while (remaining >= xpToNextLevel(level)) {
    remaining -= xpToNextLevel(level);
    level += 1;
    if (level > 500) break; // sanity ceiling
  }
  return { level, intoLevel: remaining, toNext: xpToNextLevel(level) };
};

// ------------------------------------------------------------
// XP SOURCES — yield = zoneLevel × multiplier. Spec table.
// Variety is rewarded: mixed play out-levels pure grinding.
// ------------------------------------------------------------
export const XP_YIELD = {
  enemyKill: 5,
  eliteKill: 30,
  dungeonBoss: 200,
  worldBoss: 1000,
  questSide: 100,
  questStory: 500,
  easterEggStep: 50, // mythic final steps range up to ×1000
  easterEggMythicFinal: 1000,
  worldEventObjective: 300,
  firstTimeInZone: 100,
  gatherNode: 2,
};

export const xpFor = (sourceKey, zoneLevel) =>
  Math.round((XP_YIELD[sourceKey] ?? 0) * zoneLevel);

// ------------------------------------------------------------
// THE WELL — Blood Crests → Renown (XP). (Jul 21 balance pass.)
// caP'n's ruling: crests LEVEL YOU UP, they do NOT buy skill
// points directly (points stay scarce/permanent and level-gated
// — that design pillar is untouched; you just reach the point-
// awarding levels faster). This is the ONE conversion rate the
// Trainer's Well reads. Spend routes through deltaBloodMoney;
// the XP lands through deltaXp — both already delta-transacted.
//
// DIAL: CREST_TO_XP_RATE.
// ------------------------------------------------------------
export const CREST_TO_XP_RATE = 10; // XP granted per Blood Crest spent

/** XP a given crest spend buys. */
export const xpForCrests = (crests) =>
  Math.max(0, Math.floor(crests)) * CREST_TO_XP_RATE;

/** Crests needed to buy a given XP amount (rounded up). */
export const crestsForXp = (xp) =>
  Math.max(0, Math.ceil(xp / CREST_TO_XP_RATE));

/**
 * "Fill to next level" helper — given current lifetime xp, how
 * much XP remains to the next level and what that costs in crests.
 * @returns {{ xpNeeded: number, crestCost: number, level: number }}
 */
export const fillToNextLevel = (xp) => {
  const { level, intoLevel, toNext } = levelForXp(xp || 0);
  const xpNeeded = Math.max(0, toNext - intoLevel);
  return { xpNeeded, crestCost: crestsForXp(xpNeeded), level };
};

// ------------------------------------------------------------
// SKILL POINTS — awarded on a schedule separate from levels.
//   Beb: every ODD level (1,3,...,29)      → 15 points in tier
//   Champion: every 3rd level (33,...,60)  → 10 points in tier
//   Lord: every 5th level (65,70,...)      → slow trickle forever
// Level 60 ⇒ 25 total · level 100 ⇒ 33 · level 200 ⇒ 53.
//
// THE POINT COST is now Gate C of the Doctrine's four gates —
// with tier gating dead, scarcity is doing more of the work than
// it used to. Untouched here on purpose.
// ------------------------------------------------------------
export const levelAwardsSkillPoint = (level) => {
  if (level <= 29) return level % 2 === 1;
  if (level <= 60) return level > 30 && level % 3 === 0;
  return level % 5 === 0;
};

/** Total skill points earned by a given level (inclusive). */
export const skillPointsAtLevel = (level) => {
  let pts = 0;
  for (let l = 1; l <= level; l++) if (levelAwardsSkillPoint(l)) pts += 1;
  return pts;
};

// ------------------------------------------------------------
//  ⚑⚑ THE VIGIL — THE CHAO GROWTH LAW, IN REAL TIME ⚑⚑
//  (POWER DOCTRINE — the Empire's second Pillar, finally here.)
//
//  Terror's most elegant rule: spend zero Chao in a turn and
//  your Max Chao permanently rises by 1. Restraint compounds.
//  Expenditure freezes growth. The skill says to port this above
//  almost everything, and until now Annihilation's Chao was a
//  mana bar with an Empire name on it.
//
//  Annihilation has no turns, so the beat is THE ENGAGEMENT —
//  opens on first aggro, closes when the last aggroed enemy is
//  ruined or fully de-aggros.
//
//    > THE VIGIL. An engagement completed without spending a
//    > single point of Chao is a Vigil. Completing one raises
//    > that character's Max Chao by VIGIL_GRANT, permanently.
//
//  Lyt is free of this law entirely — a Vigil is a Lyt-only
//  fight, and you may swing forever. Wearing a Primera does not
//  break it either (it costs nothing to WEAR). Anything that
//  actually spends Chao does: Impera, U'hlti, Tome fire, Sense,
//  Instinct, Shadow, Echo, Mirage.
//
//  ⚑ THE ANTI-FARM CLAUSE — the part that makes it good.
//  A Vigil only counts if your Anima was dimmed at least once
//  during the engagement. Without it, a Lord farms Esclings in
//  the Old Fold with a rubber band on the swing key. With it,
//  RESTRAINT ONLY PAYS WHEN RESTRAINT WAS DANGEROUS — which is
//  the exact tension Terror's version gets for free from having
//  an opponent.
//
//  The Vigil is ALSO a Decree trigger (combat.js DECREE_TRIGGERS
//  .vigil), so restraint is not a slow-burn economy decision —
//  it is a live combat buff the player feels break the frame
//  they cast. That closes the loop Terror never had to close.
//
//  THE CIRCUMVENTION LAW still applies: VIGIL_CEILING is the
//  NATURAL limit. Items, Tomes and Heresy push past it freely.
//
//  DIALS: all three numbers.
// ------------------------------------------------------------
export const VIGIL_GRANT = 2; //     Max Chao gained per Vigil
export const VIGIL_CEILING = 40; //  natural cap on lifetime Vigil gains
export const VIGIL_REQUIRES_HARM = true; // the anti-farm clause

/** Chao a character has earned through Vigils, clamped to the natural ceiling. */
export const vigilChaoBonus = (vigilsHeld = 0) =>
  Math.min(VIGIL_CEILING, Math.max(0, Math.floor(vigilsHeld)) * VIGIL_GRANT);

/**
 * Does a closed engagement qualify as a Vigil?
 * One predicate, called from exactly one place in the controller,
 * so the law can never drift into two half-implementations.
 * @param {{ chaoSpent: number, animaDimmed: boolean }} engagement
 */
export const isVigil = ({ chaoSpent = 0, animaDimmed = false } = {}) =>
  chaoSpent <= 0 && (!VIGIL_REQUIRES_HARM || animaDimmed);

// ------------------------------------------------------------
// CHAO REGEN — passive, tier-based. Spec gives Beb = 2/sec
// (cadence: small Primera every ~3s, Impera after ~7s build-up).
// Champion/Lord base rates: transcribe from Systems Spec Part 2
// on the next doc pass — placeholders scale gently, flagged.
// ------------------------------------------------------------
export const CHAO_REGEN_PER_SEC = {
  [TIERS.BEB]: 2,
  [TIERS.CHAMPION]: 3, // TODO(spec transcription): confirm vs doc
  [TIERS.LORD]: 4, //     TODO(spec transcription): confirm vs doc
};

// Lyt regen: full pool back within ~1.8 seconds of not attacking.
// ⚑ Jul 21 balance pass: 2.5 → 1.8 (swarm relief — keep swinging
// through a crowd). DIAL: this single number.
export const LYT_FULL_REGEN_SECONDS = 1.8;
